import { apiRequest, buildURL } from './commons/CommonService.Functions';
import { GetDebtsByCreditor } from './CashFlow-Debts.Services';
import { GetPaymentsByPayer } from './CashFlow-Payments.Services';

// functions
import {
    groupDebtsBySettlement,
    isNotFound,
    paymentToCreditorPayment,
    sortPaymentsByDateDesc
} from '../functions/PractitionerAccountStatement.Functions';

// models
import { Debt } from '../models/Entities/CashFlow/Debt/Debt';
import { Settlement } from '../models/Entities/Settlements/Settlement';
import {
    CreditorPayment,
    PractitionerPatientsSummary,
    PractitionerVisitsSummary,
    SettlementDebtSummary,
    WithSource
} from '../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';

/**
 * Servicios del estado de cuenta del profesional (contrato §11).
 * Los endpoints nuevos de CashFlow (§11.1) y MedicalProtocols (§11.2) pueden no
 * estar publicados todavía: con 404 se usa un reemplazo o se informa que el dato
 * no está disponible, sin romper la pantalla.
 */

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;
const PaymentsURL = Gateway + 'cashflow/payments';
const DebtsURL = Gateway + 'cashflow/debts';
const SettlementsURL = Gateway + 'settlements/settlements/';
const ProtocolsURL = Gateway + 'protocols/protocols';

export interface CreditorPaymentsFilter {
    debtorId?: string;
    dateFrom?: Date;
    dateTo?: Date;
    currencyCode?: string;
}

const query = (params: Record<string, string | undefined>): string => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value) search.append(key, value);
    });
    const text = search.toString();
    return text ? `?${text}` : '';
};

// ── CashFlow §11.1 ──

/** GET payments/imputed-to-creditor/{CreditorId}: pagos imputados a deudas del profesional, sin importar el receptor. */
export const GetPaymentsImputedToCreditor = async (
    token: string,
    creditorId: string,
    filter: CreditorPaymentsFilter = {}
): Promise<CreditorPayment[]> => {
    const url = buildURL(PaymentsURL, 'imputed-to-creditor', creditorId) + query({
        DebtorId: filter.debtorId,
        DateFrom: filter.dateFrom?.toJSON(),
        DateTo: filter.dateTo?.toJSON(),
        CurrencyCode: filter.currencyCode
    });
    return (await apiRequest<CreditorPayment[]>(url, token, 'GetPaymentsImputedToCreditor')) ?? [];
};

/** GET debts/creditor/{CreditorId}/by-settlement: lo liquidado, pagado y pendiente por liquidación y moneda. */
export const GetDebtSummaryBySettlement = async (
    token: string,
    creditorId: string,
    debtorId?: string
): Promise<SettlementDebtSummary[]> => {
    const url = buildURL(DebtsURL, 'creditor', creditorId, 'by-settlement') + query({ DebtorId: debtorId });
    return (await apiRequest<SettlementDebtSummary[]>(url, token, 'GetDebtSummaryBySettlement')) ?? [];
};

/**
 * Pagos del profesional. Sin el endpoint nuevo (404) se leen los pagos del
 * centro al profesional (`payments?payer&receiver`): faltan los hechos a otro
 * receptor y las imputaciones, y `source` lo avisa.
 */
export const GetCreditorPaymentsWithFallback = async (
    token: string,
    creditorId: string,
    debtorId: string
): Promise<WithSource<CreditorPayment[]>> => {
    try {
        const items = await GetPaymentsImputedToCreditor(token, creditorId, { debtorId });
        return { items: sortPaymentsByDateDesc(items), source: 'endpoint' };
    } catch (error: unknown) {
        if (!isNotFound(error)) throw error;
        const payments = (await GetPaymentsByPayer(token, debtorId, creditorId)) ?? [];
        return {
            items: sortPaymentsByDateDesc(payments.map((payment) => paymentToCreditorPayment(payment, creditorId))),
            source: 'fallback'
        };
    }
};

/** Resumen por liquidación. Sin el endpoint nuevo (404) se agrupan las deudas ya leídas. */
export const GetSettlementSummaryWithFallback = async (
    token: string,
    creditorId: string,
    debtorId: string,
    debts: () => Promise<Debt[]>
): Promise<WithSource<SettlementDebtSummary[]>> => {
    try {
        return { items: await GetDebtSummaryBySettlement(token, creditorId, debtorId), source: 'endpoint' };
    } catch (error: unknown) {
        if (!isNotFound(error)) throw error;
        return { items: groupDebtsBySettlement(await debts()), source: 'fallback' };
    }
};

/** Deudas del centro con el profesional (todas: pendientes y saldadas). */
export const GetPractitionerDebts = async (token: string, creditorId: string, debtorId: string): Promise<Debt[]> =>
    (await GetDebtsByCreditor(token, creditorId, debtorId)) ?? [];

// ── Settlements ──

/**
 * Liquidaciones del profesional, con nombre, período y estado. El endpoint
 * `/Practitioners/{id}/settlements` de Settlements sólo devuelve la asociación
 * (id y profesional, la liquidación va con JsonIgnore), así que se leen las
 * liquidaciones del tenant y se quedan las asociadas al profesional.
 */
export const GetPractitionerSettlements = async (token: string, practitionerId: string): Promise<Settlement[]> => {
    const all = (await apiRequest<Settlement[]>(SettlementsURL, token, 'GetPractitionerSettlements')) ?? [];
    const id = (practitionerId || '').toLowerCase();
    return all.filter(
        (settlement) =>
            (settlement.practitionerId || '').toLowerCase() === id ||
            (settlement.practitioner ?? []).some((item) => (item.practitionerId || '').toLowerCase() === id)
    );
};

// ── MedicalProtocols §11.2 ──

/** Pacientes a cargo. `undefined` si el endpoint todavía no está publicado (404). */
export const GetPractitionerPatientsSummary = async (
    token: string,
    practitionerId: string
): Promise<PractitionerPatientsSummary | undefined> => {
    try {
        const url = buildURL(ProtocolsURL, 'practitioners', practitionerId, 'patients', 'summary');
        return await apiRequest<PractitionerPatientsSummary>(url, token, 'GetPractitionerPatientsSummary');
    } catch (error: unknown) {
        if (isNotFound(error)) return undefined;
        throw error;
    }
};

/** Visitas del profesional en el período. `undefined` si el endpoint todavía no está publicado (404). */
export const GetPractitionerVisitsSummary = async (
    token: string,
    practitionerId: string,
    dateFrom?: Date,
    dateTo?: Date
): Promise<PractitionerVisitsSummary | undefined> => {
    try {
        const url = buildURL(ProtocolsURL, 'practitioners', practitionerId, 'visits', 'summary') +
            query({ DateFrom: dateFrom?.toJSON(), DateTo: dateTo?.toJSON() });
        return await apiRequest<PractitionerVisitsSummary>(url, token, 'GetPractitionerVisitsSummary');
    } catch (error: unknown) {
        if (isNotFound(error)) return undefined;
        throw error;
    }
};
